/**
 * BP-05:登录后回到被打断的地方。
 *
 * 两个来源:
 *  ① 路由内跳(ProtectedRoute)→ React Router `location.state.from.pathname`
 *  ② `httpClient` 收到 401 → **硬跳转** `window.location.replace("/login")`,
 *     router state 传不过来,只能用 sessionStorage 递(与既有 `xbgeo-session-kicked` 同范式)
 *
 * ★安全边界:只认**站内绝对路径**。`//evil.com` 也是以 `/` 开头的合法 pathname,
 * 浏览器会把它当协议相对 URL 解析 —— 不挡就是 open redirect。
 */
import { PARTNER_ADMIN_ROUTES, PARTNER_HOME } from "@/shared/lib/rbac";

export const RETURN_TO_KEY = "xbgeo-return-to";
/**
 * 1030:返回地址是**谁的**(被踢下线那一刻的 userId)。同一浏览器换账号登录时,上个账号断线处的
 * 地址(如他的草稿 ?draftId=…)不该把新账号带过去 —— 本机走查实测:B 登录后被送进 A 的草稿,404
 * 后整页挂「这份草稿已归档」红条。
 */
export const RETURN_TO_OWNER_KEY = "xbgeo-return-to-owner";

/** 存下来的返回地址只给原主用;没记主人(老版本存的)按原行为放行。 */
export function savedReturnToFor(
  userId: number | string | null | undefined,
  saved: string | null | undefined,
  owner: string | null | undefined
): string | null {
  if (!saved) return null;
  if (owner && userId != null && owner !== String(userId)) return null;
  return saved;
}

/** 站内路径判据:必须 `/` 开头、不能 `//` 开头(协议相对)、不能含控制字符。 */
export function isSafeInternalPath(p: string | null | undefined): boolean {
  if (!p || typeof p !== "string") return false;
  if (!p.startsWith("/")) return false;
  if (p.startsWith("//")) return false;
  // `/\evil.com` 在部分浏览器同样被当作协议相对;控制字符可绕过前面两条
  if (p.startsWith("/\\")) return false;
  // 控制字符能把前面两条绕过去(例如 "/\n//evil.com")。用码点判而不是正则字面量 ——
  // 正则里写裸控制字符 eslint 会红(no-control-regex),转义写法又难读。
  for (let i = 0; i < p.length; i++) {
    const c = p.charCodeAt(i);
    if (c <= 0x1f || c === 0x7f) return false;
  }
  return true;
}

/**
 * 按登录拿到的 scope 定最终落点(OEM W3 C-G):渠道管理员(PARTNER)只认渠道后台能进的路径,
 * 其余(含默认的 "/"、上次租户会话留下的 returnTo)一律落 /partner;其他 scope 原样返回。
 */
export function landingFor(scope: string | null | undefined, from: string): string {
  if (scope !== "PARTNER") return from;
  const ok = PARTNER_ADMIN_ROUTES.some(
    (p) => p !== "/403" && (from === p || from.startsWith(p + "/"))
  );
  return ok ? from : PARTNER_HOME;
}

/** 解析登录后该去哪:router state 优先,其次 sessionStorage,都没有回首页。 */
export function resolveReturnTo(
  stateFrom: string | null | undefined,
  saved: string | null | undefined
): string {
  if (isSafeInternalPath(stateFrom)) return stateFrom as string;
  if (isSafeInternalPath(saved)) return saved as string;
  return "/";
}
